import React, { useState, useEffect, useMemo } from 'react';
import { Button, Space, Card, Spin, Typography, Tabs, Steps } from 'antd';
import { SaveOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { CustomerDetailDTO, CustomerUpdateDTO } from '@/shared/api/model';
import InlineFieldGroups from '@/modules/customer-onboarding/components/InlineFieldGroups';
import SingleFieldGroupForm from '@/modules/customer-onboarding/components/SingleFieldGroupForm';
import ProductSelectionForm from '@/modules/customer-onboarding/components/ProductSelectionForm';
import LocationSelectionForm from '@/modules/customer-onboarding/components/LocationSelectionForm';
import DocumentUploadForm from '@/modules/customer-onboarding/components/DocumentUploadForm';
import PhotoSignatureUpload from '@/modules/customer-onboarding/components/PhotoSignatureUpload';
import IndividualReviewForm from '@/modules/customer-onboarding/components/IndividualReviewForm';
import { useOnboardingStore } from '@/modules/customer-onboarding/stores/onboarding-store';
import { useGetCategoryById } from '@/shared/api/client/customer-category-controller';
import { useGetAllProducts1 } from '@/shared/api/client/customer-product-controller';
import { useGetFieldConfigurationForCategoryGrouped } from '@/shared/api/client/field-configuration-controller';

const CUSTINFO_FIELDS = new Set(['ctitle', 'marital', 'housing', 'educatio', 'language', 'income', 'officer', 'kin1', 'kin2', 'relationshipManager']);

const { Title, Paragraph } = Typography;

const normalizeFormDateValues = (data: Record<string, any>, fieldConfiguration?: any) => {
  if (!fieldConfiguration?.fieldGroups) {
    return data;
  }

  const normalizedData = { ...data };
  fieldConfiguration.fieldGroups.forEach((group: any) => {
    group.fields?.forEach((field: any) => {
      if (field.type === 'date' && normalizedData[field.fieldName!]) {
        const dateValue = normalizedData[field.fieldName!];
        if (typeof dateValue === 'string' && dateValue) {
          const parsed = dayjs(dateValue);
          normalizedData[field.fieldName!] = parsed.isValid() ? parsed : null;
        } else if (dayjs.isDayjs(dateValue)) {
          normalizedData[field.fieldName!] = dateValue.isValid() ? dateValue : null;
        }
      }
    });
  });

  return normalizedData;
};

const normalizeSelectedProduct = (product: any) => ({
  productCode: product?.productCode || product?.glNo,
  glNo: product?.glNo || product?.productCode,
  productName: product?.productName || product?.glNo,
  productType: product?.productType,
  productTypeName: product?.productTypeName,
  isDefault: product?.isDefault,
  displayOrder: product?.displayOrder,
});

interface CustomerEditFormProps {
  customer: CustomerDetailDTO;
  onSave: (updatePayload: CustomerUpdateDTO) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const CustomerEditForm: React.FC<CustomerEditFormProps> = ({
  customer,
  onSave,
  onCancel,
  loading,
}) => {
  const [formData, setFormData] = useState<Record<string, any>>(customer.dynamicFieldData || {});
  const [saving, setSaving] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState<any[]>((customer.products || []).map(normalizeSelectedProduct));
  
  // Transform customer locations to LocationData format expected by LocationSelectionForm
  const [selectedLocations, setSelectedLocations] = useState<any[]>(
    (customer.locations || []).map(loc => ({
      locationConfigName: loc.addressType || 'HOME', // Use addressType as config name
      addressType: loc.addressType,
      locationSelections: {
        zone: loc.zone,
        region: loc.region,
        district: loc.district,
        ward: loc.ward,
        street: loc.street,
      },
      houseNumber: loc.houseNumber,
      postalCode: loc.postalCode,
      gpsCoordinates: loc.latitude && loc.longitude 
        ? `${loc.latitude},${loc.longitude}` 
        : undefined,
      isPrimary: loc.isPrimary || false,
      displayOrder: loc.displayOrder || 0,
    }))
  );
  
  // Map customer documents to the format expected by DocumentUploadForm
  const [selectedDocuments, setSelectedDocuments] = useState<any[]>(
    (customer.documents || []).map(doc => ({
      documentTypeCode: doc.documentType,
      documentTypeName: doc.documentTypeName,
      fileId: doc.fileId,
      fileName: doc.fileName,
      fileMetadata: {
        id: doc.fileId,
        name: doc.fileName,
        size: doc.fileSize,
        mimeType: doc.mimeType,
      },
      isRequired: doc.isRequired,
    }))
  );

  // Photo and signature state
  const [photoFileId, setPhotoFileId] = useState<string | undefined>(customer.photoFileId);
  const [signatureFileId, setSignatureFileId] = useState<string | undefined>(customer.signatureFileId);
  const [photoUploadedAt, setPhotoUploadedAt] = useState<string | undefined>(customer.photoUploadedAt);
  const [signatureUploadedAt, setSignatureUploadedAt] = useState<string | undefined>(customer.signatureUploadedAt);

  const categoryId = customer.categoryId;

  // Fetch category configuration (same as onboarding)
  const {
    data: category,
    isLoading: categoryLoading,
    error: categoryError,
  } = useGetCategoryById(Number(categoryId), {
    query: {
      enabled: !!categoryId,
    },
  });

  // Fetch field configuration (same as onboarding)
  const {
    data: fieldConfiguration,
    isLoading: fieldsLoading,
    error: fieldsError,
  } = useGetFieldConfigurationForCategoryGrouped(Number(categoryId), {
    query: {
      enabled: !!categoryId,
    },
  });

  const { data: allProducts = [] } = useGetAllProducts1({
    query: {
      staleTime: 5 * 60 * 1000,
    },
  });

  // Use onboarding store for review form
  const { updateFormData, setSteps: setOnboardingSteps } = useOnboardingStore();

  const normalizedFormData = useMemo(
    () => normalizeFormDateValues(formData, fieldConfiguration),
    [formData, fieldConfiguration],
  );

  // Calculate total number of steps for wizard mode
  const visibleGroupsCount = fieldConfiguration?.fieldGroups
    ?.filter((group) => group.isVisible).length || 0;
  const totalSteps = visibleGroupsCount + 5; // field groups + products + locations + documents + biometric + review

  useEffect(() => {
    if (customer.dynamicFieldData && fieldConfiguration) {
      setFormData(normalizeFormDateValues(customer.dynamicFieldData, fieldConfiguration));
    }
  }, [customer, fieldConfiguration]);

  // Prepare data for review form when on review step
  // This will be called when currentStepIndex changes to the last step
  useEffect(() => {
    // Check if we're on the last step (review step) and fieldConfiguration is loaded
    if (fieldConfiguration && currentStepIndex === totalSteps - 1 && totalSteps > 0) {
      // Update form data in store
      updateFormData('customerData', Object.fromEntries(Object.entries(formData).filter(([key]) => !CUSTINFO_FIELDS.has(key))));
      updateFormData('custInfoData', Object.fromEntries(Object.entries(formData).filter(([key]) => CUSTINFO_FIELDS.has(key))));
      updateFormData('productData', selectedProducts);
      updateFormData('locationData', selectedLocations);
      updateFormData('documentData', selectedDocuments);
      updateFormData('photoFileId', photoFileId);
      updateFormData('signatureFileId', signatureFileId);

      // Set steps with field groups for review
      const visibleGroups = fieldConfiguration.fieldGroups
        ?.filter((group) => group.isVisible)
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)) || [];

      const fieldGroupSteps = visibleGroups.map((group, index) => ({
        id: String(index),
        title: group.groupName || '',
        component: 'FieldGroup' as const,
        fieldGroup: group,
        isRequired: false,
        isCompleted: true,
      }));
      setOnboardingSteps(fieldGroupSteps);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldConfiguration, currentStepIndex, totalSteps]);

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);

      // Separate fields into customer and custinfo
      const customerFields = ['name', 'dobirth', 'custSex', 'address', 'phone', 'mobile', 'idCard', 'refNo', 'custCat', 'custCa2'];
      const custinfoFields = ['ctitle', 'marital', 'housing', 'educatio', 'language', 'income', 'officer', 'kin1', 'kin2', 'relationshipManager'];

      const customerData: Record<string, any> = {};
      const custInfoData: Record<string, any> = {};

      Object.keys(formData).forEach((key) => {
        let value = formData[key];
        
        // Convert dayjs objects to ISO string for API
        if (value && typeof value === 'object' && dayjs.isDayjs(value)) {
          value = value.toISOString();
        }
        
        if (customerFields.includes(key)) {
          customerData[key] = value;
        } else if (custinfoFields.includes(key)) {
          custInfoData[key] = value;
        }
      });

      // Transform locations back to CustomerLocationDTO format for backend
      // Filter out locations with no location selections (null/empty data)
      const transformedLocations = selectedLocations
        .filter(loc => {
          // Only include locations that have at least one location selection
          const hasLocationData = loc.locationSelections && 
            Object.values(loc.locationSelections).some(val => val !== null && val !== undefined);
          return hasLocationData;
        })
        .map(loc => ({
          branch: customer.customerData?.branch,
          custNo: customer.customerData?.custNo,
          addressType: loc.addressType,
          zone: loc.locationSelections?.zone,
          region: loc.locationSelections?.region,
          district: loc.locationSelections?.district,
          ward: loc.locationSelections?.ward,
          street: loc.locationSelections?.street,
          houseNumber: loc.houseNumber,
          postalCode: loc.postalCode,
          latitude: loc.gpsCoordinates ? parseFloat(loc.gpsCoordinates.split(',')[0]) : undefined,
          longitude: loc.gpsCoordinates ? parseFloat(loc.gpsCoordinates.split(',')[1]) : undefined,
          isPrimary: loc.isPrimary,
          displayOrder: loc.displayOrder,
          zoneId: loc.locationSelections?.zone?.id,
          regionId: loc.locationSelections?.region?.id,
          districtId: loc.locationSelections?.district?.id,
          wardId: loc.locationSelections?.ward?.id,
          streetId: loc.locationSelections?.street?.id,
        }));

      // Transform documents back to CustomerDocumentDTO format for backend
      const transformedDocuments = selectedDocuments.map(doc => ({
        documentType: doc.documentTypeCode, // Map documentTypeCode to documentType
        documentTypeName: doc.documentTypeName,
        fileId: doc.fileId,
        fileName: doc.fileName,
        isRequired: doc.isRequired,
        notes: doc.notes,
      }));

      const updatePayload: CustomerUpdateDTO = {
        branch: customer.customerData?.branch,
        custNo: customer.customerData?.custNo,
        customerData,
        custInfoData,
        categoryId: customer.categoryId,
        products: selectedProducts,
        locations: transformedLocations,
        documents: transformedDocuments,
        photoFileId: photoFileId,
        signatureFileId: signatureFileId,
        photoUploadedAt: photoUploadedAt,
        signatureUploadedAt: signatureUploadedAt,
      };

      await onSave(updatePayload);
    } catch (error) {
      console.error('Update error:', error);
    } finally {
      setSaving(false);
    }
  };

  if (categoryLoading || fieldsLoading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <p>Loading customer category and field configuration...</p>
        </div>
      </Card>
    );
  }

  if (categoryError || fieldsError || !category || !fieldConfiguration) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <p>Unable to load customer category or field configuration. Please try again.</p>
          <Button onClick={onCancel}>Cancel</Button>
        </div>
      </Card>
    );
  }

  // Get UI configuration from category (same as onboarding)
  const uiConfig = category?.uiConfiguration;
  const renderingMode = uiConfig?.renderingMode || 'inline'; // Default to inline for edit

  // Render action buttons
  const actionButtons = (
    <Space>
      <Button
        onClick={onCancel}
        icon={<CloseOutlined />}
        size="large"
        disabled={saving || loading}
      >
        Cancel
      </Button>
      <Button
        type="primary"
        onClick={handleSubmit}
        icon={<SaveOutlined />}
        loading={saving || loading}
        size="large"
      >
        Save Changes
      </Button>
    </Space>
  );

  // Render based on UI configuration mode
  if (renderingMode === 'tabs') {
    // Create tabs from field groups
    const tabItems = (fieldConfiguration.fieldGroups || [])
      .filter((group) => group.isVisible)
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
      .map((group) => ({
        key: group.groupKey || '',
        label: group.groupName || '',
        children: (
          <SingleFieldGroupForm
            fieldGroup={group}
            formData={normalizedFormData}
            onFieldChange={handleFieldChange}
            errors={{}}
            categoryId={categoryId}
          />
        ),
      }));

    tabItems.push({
      key: 'products',
      label: 'Products',
      children: (
        <ProductSelectionForm
          categoryProducts={category?.products || []}
          selectedProducts={selectedProducts}
          onProductsChange={setSelectedProducts}
          allProducts={allProducts}
          allowAdditionalSelection={true}
        />
      ),
    });

    return (
      <div className="customer-edit-form">
        <Card>
          <div style={{ marginBottom: 24 }}>
            <Title level={4}>Edit Customer Information</Title>
            <Paragraph type="secondary">
              Update customer details for {customer.customerData?.name}
            </Paragraph>
          </div>

          <Tabs items={tabItems} />

          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
            {actionButtons}
          </div>
        </Card>
      </div>
    );
  }

  if (renderingMode === 'wizard') {
    // For wizard mode, render groups sequentially with navigation
    const visibleGroups = fieldConfiguration.fieldGroups
      ?.filter((group) => group.isVisible)
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)) || [];
    
    // Check if biometric group already exists in field groups
    const hasBiometricGroup = visibleGroups.some(
      (group) => group.groupKey?.toLowerCase().includes('biometric') || 
                 group.groupName?.toLowerCase().includes('biometric')
    );
    
    // Build all steps: field groups + product + location + document + biometric (if not in groups) + review
    const allSteps = [
      ...visibleGroups.map((group) => ({
        title: group.groupName,
        description: group.description,
        type: 'fieldGroup' as const,
        data: group,
      })),
      {
        title: 'Product Selection',
        description: 'Select products and services for the customer',
        type: 'products' as const,
      },
      {
        title: 'Address Information',
        description: 'Customer address and location details',
        type: 'locations' as const,
      },
      {
        title: 'Document Upload',
        description: 'Upload required documents',
        type: 'documents' as const,
      },
      // Only add biometric step if not already in field groups
      ...(!hasBiometricGroup ? [{
        title: 'Biometric Information',
        description: 'Customer photo and signature',
        type: 'biometric' as const,
      }] : []),
      {
        title: 'Review & Submit',
        description: 'Review all information before submission',
        type: 'review' as const,
      },
    ];

    const currentStep = allSteps[currentStepIndex];

    // Create step items for the indicator
    const stepItems = allSteps.map((step) => ({
      title: step.title,
      description: step.description,
    }));

    // Render current step content
    const renderStepContent = () => {
      if (!currentStep) return null;

      switch (currentStep.type) {
        case 'fieldGroup':
          // Check if this is a biometric field group
          const isBiometricGroup = currentStep.data.groupKey?.toLowerCase().includes('biometric') || 
                                   currentStep.data.groupName?.toLowerCase().includes('biometric');
          
          if (isBiometricGroup) {
            // Render PhotoSignatureUpload for biometric groups
            return (
              <PhotoSignatureUpload
                photoFileId={photoFileId}
                signatureFileId={signatureFileId}
                onPhotoChange={(fileId, uploadedAt) => {
                  setPhotoFileId(fileId);
                  setPhotoUploadedAt(uploadedAt);
                }}
                onSignatureChange={(fileId, uploadedAt) => {
                  setSignatureFileId(fileId);
                  setSignatureUploadedAt(uploadedAt);
                }}
                onPhotoRemove={() => {
                  setPhotoFileId(undefined);
                  setPhotoUploadedAt(undefined);
                }}
                onSignatureRemove={() => {
                  setSignatureFileId(undefined);
                  setSignatureUploadedAt(undefined);
                }}
              />
            );
          }
          
          return (
            <SingleFieldGroupForm
              fieldGroup={currentStep.data}
              formData={normalizedFormData}
              onFieldChange={handleFieldChange}
              errors={{}}
              categoryId={categoryId}
            />
          );

        case 'products':
          const categoryProducts = category?.products || [];
          const customerProducts = selectedProducts || [];
          
          const missingProducts = categoryProducts.filter(
            (catProd) => !customerProducts.some((custProd) => (custProd.glNo || custProd.productCode) === catProd.glNo)
          );

          return (
            <div>
              {missingProducts.length > 0 && (
                <Card style={{ marginBottom: 16, backgroundColor: '#e6f7ff', borderColor: '#1890ff' }}>
                  <Typography.Text strong>
                    ℹ️ The following products will be added to this customer:
                  </Typography.Text>
                  <ul style={{ marginTop: 8, marginBottom: 0 }}>
                    {missingProducts.map((prod) => (
                      <li key={prod.id}>{prod.productName} ({prod.productType})</li>
                    ))}
                  </ul>
                </Card>
              )}
              <ProductSelectionForm
                categoryProducts={categoryProducts}
                selectedProducts={selectedProducts}
                onProductsChange={setSelectedProducts}
                allProducts={allProducts}
                allowAdditionalSelection={true}
              />
            </div>
          );

        case 'locations':
          return (
            <LocationSelectionForm
              categoryLocations={category?.locations || []}
              selectedLocations={selectedLocations}
              onLocationsChange={setSelectedLocations}
            />
          );

        case 'documents':
          return (
            <DocumentUploadForm
              categoryDocuments={category?.documents || []}
              selectedDocuments={selectedDocuments}
              onDocumentsChange={setSelectedDocuments}
            />
          );

        case 'biometric':
          return (
            <PhotoSignatureUpload
              photoFileId={photoFileId}
              signatureFileId={signatureFileId}
              onPhotoChange={(fileId, uploadedAt) => {
                setPhotoFileId(fileId);
                setPhotoUploadedAt(uploadedAt);
              }}
              onSignatureChange={(fileId, uploadedAt) => {
                setSignatureFileId(fileId);
                setSignatureUploadedAt(uploadedAt);
              }}
              onPhotoRemove={() => {
                setPhotoFileId(undefined);
                setPhotoUploadedAt(undefined);
              }}
              onSignatureRemove={() => {
                setSignatureFileId(undefined);
                setSignatureUploadedAt(undefined);
              }}
            />
          );

        case 'review':
          return (
            <IndividualReviewForm
              categoryId={categoryId!}
              categoryProducts={category?.products}
              categoryLocations={category?.locations}
              categoryDocuments={category?.documents}
            />
          );

        default:
          return null;
      }
    };

    return (
      <div className="customer-edit-form">
        <Card>
          <div style={{ marginBottom: 24 }}>
            <Title level={4}>Edit Customer Information</Title>
            <Paragraph type="secondary">
              Update customer details for {customer.customerData?.name}
            </Paragraph>
          </div>

          <div style={{ display: 'flex', gap: 24 }}>
            {/* Side step indicator - boxed */}
            <Card style={{ minWidth: 250, height: 'fit-content' }}>
              <Steps
                current={currentStepIndex}
                direction="vertical"
                items={stepItems}
                onChange={(current) => setCurrentStepIndex(current)}
              />
            </Card>

            {/* Form content - boxed */}
            <Card style={{ flex: 1 }}>
              {renderStepContent()}
            </Card>
          </div>

          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
            <Space>
              <Button
                onClick={() => setCurrentStepIndex(Math.max(0, currentStepIndex - 1))}
                disabled={currentStepIndex === 0}
              >
                Previous
              </Button>
              <Button
                onClick={() => setCurrentStepIndex(Math.min(allSteps.length - 1, currentStepIndex + 1))}
                disabled={currentStepIndex === allSteps.length - 1}
              >
                Next
              </Button>
            </Space>
            {actionButtons}
          </div>
        </Card>
      </div>
    );
  }

  // Default: inline mode (all groups on one page)
  return (
    <div className="customer-edit-form">
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Title level={4}>Edit Customer Information</Title>
          <Paragraph type="secondary">
            Update customer details for {customer.customerData?.name}
          </Paragraph>
        </div>

        <InlineFieldGroups
          fieldConfiguration={fieldConfiguration}
          formData={normalizedFormData}
          onFieldChange={handleFieldChange}
          errors={{}}
          readonly={false}
          categoryId={categoryId}
        />

        <div style={{ marginTop: 24 }}>
          <ProductSelectionForm
            categoryProducts={category?.products || []}
            selectedProducts={selectedProducts}
            onProductsChange={setSelectedProducts}
            allProducts={allProducts}
            allowAdditionalSelection={true}
          />
        </div>

        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
          {actionButtons}
        </div>
      </Card>
    </div>
  );
};

export default CustomerEditForm;
